import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../../components/AppLayout";
import { api } from "../../api/client";
import { Search, X, Save, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AdminCustomers() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("All");
    const [drawer, setDrawer] = useState(null); // selected customer
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = async () => {
        try { setCustomers(await api.get("/admin/customers")); } catch { toast.error("Failed to load"); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => customers.filter((c) => {
        if (filter !== "All" && c.status !== filter) return false;
        if (query && !(c.name + c.email).toLowerCase().includes(query.toLowerCase())) return false;
        return true;
    }), [customers, query, filter]);

    const exportCSV = () => {
        const header = ["id", "name", "email", "courses_purchased", "total_progress", "spent", "status", "joined"];
        const rows = filtered.map((c) => header.map((h) => `"${(c[h] ?? "").toString().replace(/"/g, '""')}"`).join(","));
        const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `customers_${Date.now()}.csv`; a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV exported");
    };

    return (
        <AppLayout searchPlaceholder="Search customers, courses...">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Customers</h1>
                    <p className="text-sm text-gray-400 mt-2">{customers.length} total registered users</p>
                </div>
                <button data-testid="export-customers" onClick={exportCSV} className="px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5">Export CSV</button>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input data-testid="search-customers" value={query} onChange={(e) => setQuery(e.target.value)}
                        placeholder="Filter by name or email..."
                        className="w-full bg-ink-700 border border-white/5 rounded-full pl-11 pr-5 py-3 text-sm focus:outline-none focus:border-lime/40" />
                </div>
                {["All", "Active", "Inactive", "Pending"].map((f) => (
                    <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.toLowerCase()}`}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${filter === f ? "bg-white text-ink-900" : "text-gray-400 hover:text-white"}`}>{f}</button>
                ))}
            </div>

            {loading ? <div className="text-gray-500">Loading…</div> : (
                <div className="bg-ink-700 rounded-3xl border border-white/5 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-xs uppercase text-gray-500 tracking-widest bg-ink-800">
                                    <th className="text-left font-medium px-6 py-4">Customer</th>
                                    <th className="text-left font-medium px-6 py-4">Courses</th>
                                    <th className="text-left font-medium px-6 py-4">Progress</th>
                                    <th className="text-left font-medium px-6 py-4">Spent</th>
                                    <th className="text-left font-medium px-6 py-4">Joined</th>
                                    <th className="text-left font-medium px-6 py-4">Status</th>
                                    <th className="text-right font-medium px-6 py-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c) => (
                                    <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => setDrawer(c)} className="border-t border-white/5 hover:bg-ink-800/50 cursor-pointer transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <img src={c.avatar} alt={c.name} className="w-10 h-10 rounded-full object-cover" />
                                                <div>
                                                    <div className="font-semibold text-sm">{c.name}{c.is_demo && <span className="ml-2 text-[9px] uppercase tracking-widest text-gray-500">demo</span>}</div>
                                                    <div className="text-xs text-gray-500">{c.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm">{c.courses_purchased}</td>
                                        <td className="px-6 py-4 w-56">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1 h-1.5 rounded-full bg-ink-600 overflow-hidden">
                                                    <div className="h-full rounded-full bg-gradient-to-r from-grape to-lime" style={{ width: `${c.total_progress}%` }} />
                                                </div>
                                                <span className="text-xs font-semibold text-lime">{c.total_progress}%</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm font-bold">£{(c.spent || 0).toFixed(2)}</td>
                                        <td className="px-6 py-4 text-xs text-gray-400">{c.joined ? new Date(c.joined).toLocaleDateString("en-GB", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-[10px] font-bold ${
                                                c.status === "Active" ? "bg-lime/15 text-lime" :
                                                c.status === "Inactive" ? "bg-red-500/15 text-red-400" : "bg-yellow-400/15 text-yellow-400"
                                            }`}>{c.status}</span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button data-testid={`delete-customer-${c.id}`} onClick={(e) => { e.stopPropagation(); setConfirmDelete(c); }}
                                                className="w-8 h-8 rounded-full hover:bg-red-500/20 hover:text-red-400 text-gray-500 inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {filtered.length === 0 && <div className="p-10 text-center text-gray-500 text-sm">No customers match.</div>}
                </div>
            )}

            {drawer && <CustomerDrawer customer={drawer} onClose={() => setDrawer(null)} onUpdated={load} />}

            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6" onClick={() => setConfirmDelete(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-ink-700 rounded-3xl border border-white/10 p-7">
                        <h3 className="font-display font-bold text-xl">Remove customer?</h3>
                        <p className="text-sm text-gray-400 mt-2">This will delete <strong>{confirmDelete.name}</strong> and revoke all their enrollments. Cannot be undone.</p>
                        <div className="mt-5 flex justify-end gap-3">
                            <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-full border border-white/10 text-sm">Cancel</button>
                            <button data-testid="confirm-delete-customer" onClick={async () => {
                                try { await api.del(`/admin/customers/${confirmDelete.id}`); toast.success("Customer removed"); setConfirmDelete(null); load(); }
                                catch { toast.error("Failed"); }
                            }} className="px-4 py-2 rounded-full bg-red-500 text-white text-sm font-bold">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </AppLayout>
    );
}

function CustomerDrawer({ customer, onClose, onUpdated }) {
    const [detail, setDetail] = useState(null);
    const [status, setStatus] = useState(customer.status || "Active");
    const [role, setRole] = useState(customer.role || "student");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/admin/customers/${customer.id}`).then(setDetail).catch(() => setDetail(customer));
    }, [customer.id]); // eslint-disable-line

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/customers/${customer.id}`, { status, role });
            toast.success("Customer updated");
            onUpdated();
        } catch { toast.error("Failed"); }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-ink-700 border-l border-white/10 overflow-y-auto">
                <div className="p-6 border-b border-white/5 flex items-center justify-between">
                    <h2 className="font-display font-bold text-xl">Customer details</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6">
                    <div className="flex items-center gap-4 mb-6">
                        <img src={customer.avatar} alt={customer.name} className="w-16 h-16 rounded-full object-cover" />
                        <div>
                            <div className="font-display font-bold text-xl">{customer.name}</div>
                            <a href={`mailto:${customer.email}`} className="text-sm text-gray-400 inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {customer.email}</a>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-ink-800 rounded-2xl p-4">
                            <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">Spent</div>
                            <div className="font-display font-black text-2xl">£{(customer.spent || 0).toFixed(2)}</div>
                        </div>
                        <div className="bg-ink-800 rounded-2xl p-4">
                            <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">Avg. progress</div>
                            <div className="font-display font-black text-2xl text-lime">{customer.total_progress}%</div>
                        </div>
                    </div>

                    <div className="space-y-4 mb-6">
                        <div>
                            <label className="text-xs uppercase tracking-widest text-gray-500 mb-2 block">Status</label>
                            <Select value={status} onValueChange={setStatus}>
                                <SelectTrigger data-testid="edit-status" className="bg-ink-800 border-white/5 rounded-xl h-11"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-ink-800 border-white/10 text-white">
                                    {["Active", "Inactive", "Pending"].map((x) => <SelectItem key={x} value={x} className="focus:bg-ink-700 focus:text-white">{x}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-widest text-gray-500 mb-2 block">Role</label>
                            <Select value={role} onValueChange={setRole}>
                                <SelectTrigger data-testid="edit-role" className="bg-ink-800 border-white/5 rounded-xl h-11"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-ink-800 border-white/10 text-white">
                                    {["student", "instructor", "admin"].map((x) => <SelectItem key={x} value={x} className="focus:bg-ink-700 focus:text-white">{x}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {detail?.enrollments?.length > 0 && (
                        <div className="mb-6">
                            <h3 className="font-bold text-sm mb-3">Enrollments ({detail.enrollments.length})</h3>
                            <div className="space-y-2">
                                {detail.enrollments.map((e) => (
                                    <div key={e.course_id} className="flex items-center gap-3 bg-ink-800 rounded-xl p-3">
                                        {e.course_thumbnail && <img src={e.course_thumbnail} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold truncate">{e.course_title}</div>
                                            <div className="text-xs text-gray-500">{e.progress}% complete</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {detail?.certificates?.length > 0 && (
                        <div className="mb-6">
                            <h3 className="font-bold text-sm mb-3">Certificates ({detail.certificates.length})</h3>
                            <div className="space-y-2">
                                {detail.certificates.map((c) => (
                                    <div key={c.id} className="bg-ink-800 rounded-xl p-3">
                                        <div className="text-sm font-semibold">{c.course_title}</div>
                                        <div className="text-xs text-gray-500 font-mono">{c.code} • Grade {c.grade}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 sticky bottom-0 bg-ink-700 pt-4">
                        <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 text-sm">Cancel</button>
                        <button data-testid="save-customer" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
